import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import VitalsTimeline from '../components/Debrief/VitalsTimeline';

interface RunData {
  run: any;
  scenario: any;
  debrief: any;
  events: any[];
  feedback: any[];
  vitals?: any[];
}

const OUTCOME_LABEL: Record<string, { text: string; color: string }> = {
  ROSC:    { text: 'ROSC achieved',     color: 'text-mono-green' },
  death:   { text: 'Death',             color: 'text-alarm-red' },
  timeout: { text: 'Timeout',           color: 'text-alarm-amber' },
  aborted: { text: 'Aborted',           color: 'text-mono-white/60' },
};

const SEVERITY_STYLES: Record<string, string> = {
  strength: 'bg-mono-green/10 text-mono-green ring-1 ring-mono-green/40',
  minor:    'bg-alarm-amber/10 text-alarm-amber ring-1 ring-alarm-amber/40',
  major:    'bg-alarm-red/10 text-alarm-red ring-1 ring-alarm-red/40',
  critical: 'bg-alarm-red/20 text-alarm-red ring-1 ring-alarm-red/70',
};

const SEV_RANK: Record<string, number> = { critical: 0, major: 1, minor: 2, strength: 3 };

export default function Debrief() {
  const { run_id } = useParams();
  const [data, setData] = useState<RunData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (!run_id) return;
    let mounted = true;
    let intervalId: number | null = null;

    async function load() {
      try {
        const r = await api.runById(run_id!);
        if (!mounted) return;
        setData(r);
        // If debrief is null but the run has ended, poll for the renderer to catch up.
        if (!r.debrief?.rendered_md && r.run?.ended_at) {
          setPolling(true);
          if (intervalId == null) intervalId = window.setInterval(load, 2000) as unknown as number;
        } else if (r.debrief?.rendered_md && intervalId != null) {
          window.clearInterval(intervalId);
          intervalId = null;
          setPolling(false);
        }
      } catch (e: any) {
        setErr(e.message || 'Failed to load run');
      }
    }
    load();
    return () => { mounted = false; if (intervalId != null) window.clearInterval(intervalId); };
  }, [run_id]);

  if (err) return <div className="max-w-4xl mx-auto p-8 text-alarm-red">{err}</div>;
  if (!data) return <div className="max-w-4xl mx-auto p-8 text-mono-white/60">Loading run…</div>;

  const outcome = data.run?.outcome || 'aborted';
  const outcomeInfo = OUTCOME_LABEL[outcome] || OUTCOME_LABEL.aborted;
  const score = data.run?.score;
  const fb = (data.feedback || []).slice().sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9));
  const strengths = fb.filter((f) => f.severity === 'strength');
  const issues = fb.filter((f) => f.severity !== 'strength');

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <p className="text-mono-white/40 text-xs uppercase tracking-widest">Debrief</p>
          <h1 className="text-2xl font-semibold mt-1">{data.scenario?.title || 'Run'}</h1>
          <p className="text-mono-white/50 text-sm mt-1">
            {data.scenario?.region} · {data.run?.difficulty} · started {new Date(data.run?.started_at).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {data.vitals && data.vitals.length > 0 && (
            <Link to={`/debrief/${run_id}/replay`} className="btn-primary">▶ Watch playback</Link>
          )}
          {data.run?.ended_at && (
            <a
              href={api.runEvidencePackUrl(run_id!)}
              download
              className="btn-ghost"
              title="Downloadable CPD evidence pack (PDF) — supervisor sign-off field, action timeline, debrief"
            >
              ⤓ CPD evidence (PDF)
            </a>
          )}
          <Link to="/dashboard" className="btn-ghost">Back to dashboard</Link>
        </div>
      </div>

      {/* Outcome strip */}
      <div className="panel p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs text-mono-white/40 uppercase tracking-widest">Outcome</div>
            <div className={`text-2xl font-semibold ${outcomeInfo.color}`}>{outcomeInfo.text}</div>
          </div>
          {typeof score === 'number' && (
            <div className="flex gap-6">
              <Metric label="Overall score" value={`${score}/100`} accent="text-mono-white" />
              {data.run?.time_to_cpr_ms != null && <Metric label="Time to CPR" value={`${(data.run.time_to_cpr_ms / 1000).toFixed(0)}s`} />}
              {data.run?.time_to_first_shock_ms != null && <Metric label="Time to first shock" value={`${(data.run.time_to_first_shock_ms / 1000).toFixed(0)}s`} />}
              {data.run?.ccf_pct != null && <Metric label="CCF" value={`${data.run.ccf_pct.toFixed(0)}%`} />}
            </div>
          )}
        </div>
      </div>

      {/* Hidden diagnosis reveal (only once run has ended) */}
      {data.scenario?.hidden_diagnosis && (
        <div className="panel p-5 mb-6 border-l-4 border-brand-600">
          <div className="text-xs text-mono-white/40 uppercase tracking-widest mb-1">Hidden diagnosis (post-run reveal)</div>
          <div className="text-mono-white">{data.scenario.hidden_diagnosis}</div>
        </div>
      )}

      {/* Vitals timeline */}
      {data.vitals && data.vitals.length > 0 && (
        <div className="mb-6">
          <VitalsTimeline vitals={data.vitals} events={data.events} />
        </div>
      )}

      {/* Rendered debrief markdown */}
      <div className="panel p-6 mb-6 whitespace-pre-wrap font-sans leading-relaxed text-mono-white/90">
        {data.debrief?.rendered_md ? (
          data.debrief.rendered_md
        ) : (
          <div className="text-mono-white/60 text-sm italic">
            {polling ? 'Rendering debrief… (this usually takes 1–2 seconds)' : 'Debrief not yet rendered.'}
          </div>
        )}
      </div>

      {/* Feedback breakdown */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="panel p-5">
          <h3 className="text-mono-green font-semibold mb-3">What you did well</h3>
          {strengths.length === 0 ? (
            <p className="text-mono-white/40 text-sm italic">No strengths recorded yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {strengths.map((s) => (
                <li key={s.id} className={`p-2 rounded ${SEVERITY_STYLES[s.severity]}`}>{s.message}</li>
              ))}
            </ul>
          )}
        </div>
        <div className="panel p-5">
          <h3 className="text-alarm-red font-semibold mb-3">What to work on</h3>
          {issues.length === 0 ? (
            <p className="text-mono-white/40 text-sm italic">No issues flagged.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {issues.map((s) => (
                <li key={s.id} className={`p-2 rounded ${SEVERITY_STYLES[s.severity]}`}>
                  <span className="text-[10px] uppercase tracking-widest mr-2 opacity-70">{s.severity}</span>
                  {s.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Timeline of events */}
      <h2 className="mt-8 mb-3 text-mono-white/60 uppercase tracking-widest text-xs">Timeline · {data.events.length} events</h2>
      <div className="panel p-4 max-h-72 overflow-auto font-mono text-xs space-y-1">
        {data.events.map((e: any, i: number) => (
          <div key={i} className="flex gap-3">
            <span className="text-mono-white/40 w-14 tabular-nums">{(e.t_ms / 1000).toFixed(0)}s</span>
            <span className="text-mono-cyan w-32 shrink-0">{e.actor_role || '—'}</span>
            <span className="text-mono-white">{e.action_type}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="text-right">
      <div className="text-[10px] text-mono-white/40 uppercase tracking-widest">{label}</div>
      <div className={`text-xl font-mono tabular-nums ${accent || 'text-mono-white/85'}`}>{value}</div>
    </div>
  );
}
